/**
 * Position Retirement — review + BULK-retire stale positions (the 500+ backlog, not 1:1).
 *
 * Candidates = vacant positions NOT linked to a funded budget line ("unbudgeted"), plus the
 * expired / zero-validity / long-vacant signals. Retirable (unbudgeted) sort first. Bulk
 * select -> dry-run preview (how many actually retire vs are refused for being filled/budgeted)
 * -> confirm -> chunked retire (each is an F&O DELETE + platform RETIRED, individually
 * safeguarded). Read paths reuse budgetService.getStalePositions.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, RefreshCw, AlertTriangle, Trash2, ShieldAlert, Search, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useConfirm } from '@/hooks/useConfirm';
import {
  budgetService, StalePositionCandidate, StalePositionsResponse, RetirePositionResult,
} from '@/services/api/organizationService';

const REASON_META: Record<string, { label: string; tone: string }> = {
  unbudgeted: { label: 'Unbudgeted', tone: 'border-amber-200 bg-amber-50 text-amber-700' },
  expired: { label: 'Expired', tone: 'border-red-200 bg-red-50 text-red-700' },
  zero_validity: { label: 'Bad dates', tone: 'border-red-200 bg-red-50 text-red-700' },
  long_vacant: { label: 'Long vacant', tone: 'border-gray-200 bg-gray-50 text-gray-600' },
  long_vacant_since_vacated: { label: 'Long vacant', tone: 'border-gray-200 bg-gray-50 text-gray-600' },
};
const AGE_OPTS = [
  { v: 18, label: '18 months+' },
  { v: 36, label: '3 years+' },
  { v: 60, label: '5 years+' },
];

// Flag filter — "Unbudgeted" (= retirable: not linked to a funded budget line) is the default.
const FLAG_FILTERS = [
  { v: 'unbudgeted', label: 'Unbudgeted (retirable)' },
  { v: 'held', label: 'Establishment hold' },
  { v: 'requested', label: 'Waiting for a position request' },
  { v: 'long_vacant', label: 'Long vacant' },
  { v: 'expired', label: 'Expired / bad dates' },
  { v: 'all', label: 'All flags' },
];
// A seat kept on purpose (an establishment hold, or a position request still waiting to
// fill it) is listed but never selectable: the server refuses it either way.
const isKept = (c: StalePositionCandidate): boolean => !!c.held || !!c.requested;
const isRetirable = (c: StalePositionCandidate): boolean => !!c.unbudgeted && !isKept(c);

const matchesFlag = (c: StalePositionCandidate, f: string): boolean => {
  if (f === 'all') return true;
  if (f === 'unbudgeted') return isRetirable(c);
  if (f === 'held') return !!c.held;
  if (f === 'requested') return !!c.requested;
  if (f === 'long_vacant') return c.reasons.some(r => r.startsWith('long_vacant'));
  if (f === 'expired') return c.reasons.includes('expired') || c.reasons.includes('zero_validity');
  return true;
};

const PositionRetirement: React.FC = () => {
  const { confirm: showConfirm } = useConfirm();
  const [data, setData] = useState<StalePositionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [minAge, setMinAge] = useState(18);
  const [flagFilter, setFlagFilter] = useState<string>('unbudgeted');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await budgetService.getStalePositions(minAge);
      setData(res);
      setSelected(new Set());
    } catch (e) {
      console.error('Failed to load stale positions', e);
    } finally {
      setLoading(false);
    }
  }, [minAge]);
  useEffect(() => { load(); }, [load]);

  const candidates = data?.candidates || [];
  const [holdBusy, setHoldBusy] = useState<number | null>(null);
  const toggleHold = async (c: StalePositionCandidate) => {
    const lifting = !!c.held;
    const reason = window.prompt(lifting
      ? `Lift the establishment hold on ${c.fo_position_id}? Reason:`
      : `Hold ${c.fo_position_id} for the approved establishment? Reason (goes on the ledger):`,
      c.hold_reason || '');
    if (reason == null || reason.trim().length < 5) return;
    setHoldBusy(c.position_id);
    try {
      await budgetService.setEstablishmentHold(c.position_id, reason.trim(), lifting);
      await load();
    } finally { setHoldBusy(null); }
  };
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return candidates.filter(c => {
      if (!matchesFlag(c, flagFilter)) return false;
      if (!q) return true;
      const hay = [c.fo_position_id, c.position_code, c.title, c.campus_code, c.department]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [candidates, flagFilter, search]);
  // Rows matching the current flag filter, ignoring the text search — so the
  // "N shown / of M" readout reflects what the search narrowed within the tab.
  const flagMatched = useMemo(
    () => candidates.filter(c => matchesFlag(c, flagFilter)),
    [candidates, flagFilter],
  );
  const retirableCount = candidates.filter(isRetirable).length;

  const toggle = (id: number) => setSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const selectableVisible = visible.filter(c => !isKept(c));
  const allVisibleSelected = selectableVisible.length > 0 && selectableVisible.every(c => selected.has(c.position_id));
  const toggleAll = () => setSelected(prev => {
    const n = new Set(prev);
    if (allVisibleSelected) selectableVisible.forEach(c => n.delete(c.position_id));
    else selectableVisible.forEach(c => n.add(c.position_id));
    return n;
  });
  const selectRetirable = () => setSelected(new Set(candidates.filter(isRetirable).map(c => c.position_id)));

  // Dry-run preview only (chunked 250, fast — no F&O writes). The LIVE retire is a background
  // job (startBulkRetireJob + poll below), so it never blocks on a long-running request.
  const runDryRunPreview = async (ids: number[]) => {
    const agg = { retired: 0, dry_run_ok: 0, refused: 0, total: 0, results: [] as RetirePositionResult[] };
    for (let i = 0; i < ids.length; i += 250) {
      const r = await budgetService.bulkRetirePositions(ids.slice(i, i + 250), true);
      agg.retired += r.retired; agg.dry_run_ok += r.dry_run_ok; agg.refused += r.refused; agg.total += r.total;
      agg.results.push(...r.results);
    }
    return agg;
  };

  const doRetire = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setMsg(null);
    setProgress({ done: 0, total: ids.length });
    let preview;
    try {
      preview = await runDryRunPreview(ids);
    } catch (e: any) {
      setProgress(null);
      setMsg({ type: 'error', text: e?.response?.data?.detail || 'Preview failed.' });
      return;
    }
    setProgress(null);
    const willRetire = preview.dry_run_ok;
    const willRefuse = preview.refused;
    // Name the actual refusal reasons — "filled or budgeted" was a guess that
    // misled the moment the skips were reporting-line anchors or held seats.
    const SKIP_LABELS: Record<string, string> = {
      filled: 'still filled', has_budget: 'carry budget',
      has_subordinates: 'anchor reporting lines', establishment_hold: 'held for establishment',
      requested_seat: 'still waiting for their position request', taken_in_fo: 'not vacant in F&O',
      already_retired: 'already retired',
    };
    const skipCounts: Record<string, number> = {};
    for (const r of preview.results) {
      if (r.retired || r.reason === 'dry_run_ok') continue;
      const label = SKIP_LABELS[r.code ?? ''] ?? 'refused';
      skipCounts[label] = (skipCounts[label] ?? 0) + 1;
    }
    const skipDetail = Object.entries(skipCounts).map(([l, n]) => `${n} ${l}`).join(', ');
    if (!willRetire) {
      setMsg({ type: 'error', text: `None of the ${ids.length} selected can retire (${skipDetail}).` });
      return;
    }
    const ok = await showConfirm(
      `Retire ${willRetire} position(s)? ${willRefuse > 0 ? `${willRefuse} will be skipped (${skipDetail}). ` : ''}` +
      `This expires them in F&O and marks them RETIRED — it can't be bulk-undone.`,
      'Retire stale positions',
      { confirmText: `Retire ${willRetire}`, type: 'warning' },
    );
    if (!ok) return;

    setProgress({ done: 0, total: ids.length });
    try {
      // Live retire runs as a background job (each seat is ~5 F&O round-trips) — poll for progress.
      const start = await budgetService.startBulkRetireJob(ids);
      setProgress({ done: 0, total: start.total });
      // A poll can transiently fail while the job keeps running server-side (e.g. a
      // replica whose job store missed the handle). Losing the progress bar must not
      // read as a failed retirement — tolerate consecutive poll errors, and if they
      // persist, say honestly that the job continues without a progress feed.
      let job: Awaited<ReturnType<typeof budgetService.getRetireJob>> | null = null;
      let pollFailures = 0;
      do {
        await new Promise(r => setTimeout(r, 2000));
        try {
          job = await budgetService.getRetireJob(start.job_id);
          pollFailures = 0;
          setProgress({ done: job.done, total: job.total });
        } catch {
          pollFailures += 1;
          if (pollFailures >= 5) {
            setMsg({
              type: 'success',
              text: `Retirement is running server-side, but its progress feed is unreachable from this session. ` +
                `Give it ~${Math.max(1, Math.ceil(start.total / 10))} min, then hit Refresh — retired seats leave the queue.`,
            });
            return;
          }
        }
      } while (!job || job.status === 'running');
      setMsg({ type: 'success', text: `Retired ${job.retired}${job.refused > 0 ? `, skipped ${job.refused} — see the queue for who and why` : ''}.` });
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || 'Retirement failed — refresh and retry the rest.' });
    } finally {
      setProgress(null);
      await load();
    }
  };

  const busy = loading || !!progress;

  return (
    <div className="space-y-3">
      <div className="rounded border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 flex items-start gap-2">
        <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div>Retiring expires the position in F&O and marks it <strong>RETIRED</strong> + its budget line abolished.
          Each is safeguarded — a seat that's still <strong>filled</strong> or carries <strong>positive current/future budget</strong>
          is refused even if selected. Vacant positions <strong>not linked to a funded budget line</strong> are the retirable ones. Seats a position request is still waiting to fill are listed but never retired here.</div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search position, title, dept…"
            className="h-7 w-[220px] rounded border border-gray-200 pl-7 pr-6 text-xs focus:outline-none focus:ring-1 focus:ring-heritage-blue"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <Select value={String(minAge)} onValueChange={v => setMinAge(Number(v))}>
          <SelectTrigger className="h-7 w-[130px] text-xs" aria-label="Vacant for"><SelectValue /></SelectTrigger>
          <SelectContent>{AGE_OPTS.map(o => <SelectItem key={o.v} value={String(o.v)}>Vacant {o.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={flagFilter} onValueChange={setFlagFilter}>
          <SelectTrigger className="h-7 w-[170px] text-xs" aria-label="Flag"><SelectValue /></SelectTrigger>
          <SelectContent>{FLAG_FILTERS.map(o => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={selectRetirable} disabled={busy || !retirableCount}>
          Select all retirable ({retirableCount})
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={busy}>
            <RefreshCw className={cn('h-3.5 w-3.5 mr-1', loading && 'animate-spin')} /> Refresh
          </Button>
          <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={doRetire} disabled={busy || selected.size === 0}>
            {progress ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1" />}
            {progress ? `Retiring ${progress.done}/${progress.total}…` : `Retire selected (${selected.size})`}
          </Button>
        </div>
      </div>

      {data && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <span><strong className="text-gray-700">{data.candidate_count}</strong> candidates</span>
          {search.trim() && (
            <>
              <span>·</span>
              <span className="text-heritage-blue">{visible.length} of {flagMatched.length} shown</span>
            </>
          )}
          <span>·</span>
          {Object.entries(data.by_reason).map(([r, n]) => (
            <span key={r} className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[11px]', REASON_META[r]?.tone || 'border-gray-200 text-gray-500')}>
              {REASON_META[r]?.label || r}: {n}
            </span>
          ))}
          {!!data.requested_count && (
            <span className="inline-flex items-center rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 text-[11px] text-sky-700">
              Waiting for a position request: {data.requested_count}
            </span>
          )}
          {!!data.taken_count && (
            <span className="inline-flex items-center rounded-full border border-gray-300 bg-gray-50 px-2 py-0.5 text-[11px] text-gray-600"
              title="F&O has someone in these seats, or due to start in them, though no incumbent shows here. They are not vacant, so they are left out of this list.">
              Not vacant in F&O, left out: {data.taken_count}
            </span>
          )}
        </div>
      )}

      {msg && (
        <div className={cn('rounded p-2 text-sm flex items-center gap-2', msg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700')}>
          {msg.type === 'success' ? <Trash2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}{msg.text}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="max-h-[560px] overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 text-gray-500 border-b">
                <tr>
                  <th className="px-2 py-2 w-8"><input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} aria-label="Select all" /></th>
                  <th className="text-left px-2 py-2 font-medium">Position</th>
                  <th className="text-left px-2 py-2 font-medium">Title</th>
                  <th className="text-left px-2 py-2 font-medium">Campus · Dept</th>
                  <th className="text-right px-2 py-2 font-medium">Yrs vacant</th>
                  <th className="text-left px-2 py-2 font-medium">Flags</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c: StalePositionCandidate) => (
                  <tr key={c.position_id} className={cn('border-b last:border-0 hover:bg-gray-50', selected.has(c.position_id) && 'bg-heritage-blue/5')}>
                    <td className="px-2 py-1.5"><input type="checkbox" checked={selected.has(c.position_id)} disabled={isKept(c)} onChange={() => toggle(c.position_id)} /></td>
                    <td className="px-2 py-1.5 font-mono">{c.fo_position_id || c.position_code || '—'}</td>
                    <td className="px-2 py-1.5 max-w-[260px] truncate" title={c.title || ''}>{c.title || '—'}</td>
                    <td className="px-2 py-1.5 text-gray-500">{[c.campus_code, c.department].filter(Boolean).join(' · ') || '—'}</td>
                    <td className={cn('px-2 py-1.5 text-right tabular-nums', (c.years_vacant ?? 0) >= 5 && 'font-semibold text-red-600')}>
                      {c.years_vacant != null ? c.years_vacant : '—'}
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        {c.held && (
                          <span className="inline-flex items-center rounded-full border border-violet-300 bg-violet-50 px-1.5 py-0.5 text-[10px] text-violet-700"
                            title={c.hold_reason || 'Establishment hold'}>
                            Held — plan
                          </span>
                        )}
                        {c.requested && (
                          <span className="inline-flex items-center rounded-full border border-sky-300 bg-sky-50 px-1.5 py-0.5 text-[10px] text-sky-700"
                            title={`Position request ${c.request_number ?? ''}${c.requested_on ? ` (raised ${c.requested_on})` : ''} is still waiting to fill this seat, so it is not retired here. If that request no longer needs it, retire the seat in the Budget Workbench and confirm releasing the request.`}>
                            Requested · {c.request_number}
                          </span>
                        )}
                        {c.reasons.map(r => (
                          <span key={r} className={cn('inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px]', REASON_META[r]?.tone || 'border-gray-200 text-gray-500')}>
                            {REASON_META[r]?.label || r}
                          </span>
                        ))}
                        {(!c.requested || c.held) && <button type="button"
                          className="ml-1 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-500 hover:bg-gray-100"
                          disabled={holdBusy === c.position_id}
                          title={c.held ? 'Lift the establishment hold (ledgered)' : 'Hold for the approved establishment — excluded from retirement (ledgered)'}
                          onClick={() => toggleHold(c)}>
                          {holdBusy === c.position_id ? '…' : c.held ? 'Lift hold' : 'Hold'}
                        </button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {!visible.length && (
                  <tr><td colSpan={6} className="px-2 py-10 text-center text-gray-400">
                    {loading
                      ? 'Loading…'
                      : search.trim()
                        ? `No positions match "${search.trim()}" for this selection.`
                        : 'No retirement candidates for this selection.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PositionRetirement;
